import React, { useEffect, useState } from 'react';
import { CalendarDays, ArrowUpRight, Pencil, Info, ChevronDown } from 'lucide-react';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import CircularProgress from '../../common/CircularProgress';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { toastUtils } from '../../../hooks/useToast';
import { PROFILE_EDIT_WEBHOOK, VACATION_CREATE_WEBHOOK } from '../../../hooks/api/config';
import {
  VACATION_BLOCK_BRACKETS,
  VACATION_BLOCK_TABLE_HEADERS,
  computeVacationBlockDates,
  formatVacationBlockSentence,
  formatVacationDate,
} from '../../../utils/vacationBlockBracket';
import { useContactChannel } from '../../../hooks/useContactChannel';
import { activityTracker } from '../../../utils/activityTracker';
import { VacationPeriodFormModal, VacationPeriodModalMode, ExistingVacationPeriod } from './VacationPeriodFormModal';
import { useIsPwaApp } from '../../../pwa/PwaModeContext';

interface ProfileVacationsTabProps {
  userEmail: string;
  psychologistId: string;
  onVacationSaved?: () => void;
  /** Relanza el recorrido virtual de esta pestaña — botón sutil junto al título. */
  onGuideReplay?: () => void;
  /** true mientras el tour está en el paso que muestra el modal "Informar vacaciones"
   *  a modo de demo — abre el modal con un período ficticio de ejemplo (ver
   *  `tourDemo` en VacationPeriodFormModal) y lo cierra solo al avanzar el tour. */
  tourShowDemoModal?: boolean;
}

interface VacationPeriod {
  id: string;
  from: string;
  to?: string;
  duration?: number;
  status?: string;
  cancelled?: boolean;
  psychologist?: { email?: string; id?: string };
  blockade?: { id?: string; from: string; to?: string };
}

type VacationStatus = 'Activo' | 'Próximo' | 'Finalizado';

/**
 * Snapshot de un período cancelado ANTES de empezar — workaround del bug real de Make
 * que sobreescribe `from`/`to` con la fecha de cancelación (ver memoria del proyecto y
 * `VacationCancellationSnapshot` en psiplatform-be). Solo existe para cancelaciones
 * hechas desde el panel admin de Psima (única vía real de "Cancelar" hoy).
 */
interface CancelledVacationSnapshot {
  id: string;
  /** id del período legacy que Make muta IN PLACE al cancelar (from/to sobreescritos a
   *  la fecha de cancelación) — se usa para excluirlo con certeza del listado principal,
   *  en vez de confiar en `cancelled`/`deletedAt` (Make no los setea de forma consistente,
   *  ver memoria del proyecto). */
  legacyVacationId: string;
  originalFrom: string;
  originalTo: string;
  wasFuture: boolean;
  cancelledAt: string;
  cancelledByName: string | null;
}

/**
 * Un evento de "Modificar" — workaround del mismo espíritu que `CancelledVacationSnapshot`,
 * pero acá el registro nuevo que crea Make no tiene forma de identificarse por id (el
 * webhook no lo devuelve), así que se correlaciona por FECHA contra el período activo que
 * la psicóloga ve hoy (ver `findEditChain` y `VacationEditHistory` en psiplatform-be).
 */
interface VacationEditHistoryEntry {
  id: string;
  previousVacationId: string;
  previousFrom: string;
  previousTo: string;
  newFrom: string;
  newTo: string;
  editedBy: 'psychologist' | 'admin';
  editedByName: string | null;
  editedAt: string;
}

function parseDate(iso: string): Date {
  return new Date(iso.substring(0, 10) + 'T12:00:00');
}

function localToday(): string {
  return dateToLocalStr(new Date());
}

/** Convierte un objeto Date a string YYYY-MM-DD en timezone local (mismo criterio que
 *  la función homónima de VacationPeriodFormModal.tsx). */
function dateToLocalStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatShort(iso: string): string {
  const d = parseDate(iso);
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function computeEnd(v: VacationPeriod): Date | null {
  if (v.to) return parseDate(v.to);
  if (v.duration) return new Date(parseDate(v.from).getTime() + v.duration * 86400000);
  return null;
}

function diffDays(v: VacationPeriod): number {
  if (v.duration) return v.duration;
  const end = computeEnd(v);
  if (!end) return 0;
  return Math.round((end.getTime() - parseDate(v.from).getTime()) / 86400000);
}

function getStatus(v: VacationPeriod): VacationStatus {
  const now = new Date();
  const start = parseDate(v.from);
  const end = computeEnd(v);
  if (now >= start && (!end || now <= end)) return 'Activo';
  if (now < start) return 'Próximo';
  return 'Finalizado';
}

// Mismo tono naranja que el tag "Vacaciones & días off" del banner de bloqueo (PsychologistProfile.tsx).
const STATUS_STYLES: Record<VacationStatus, string> = {
  Activo:    'bg-orange-100 text-orange-600 border border-orange-300',
  Próximo:   'bg-[#EDE9FA] text-[#6D57BC]',
  Finalizado:'bg-gray-100 text-gray-500',
};

/** Texto mostrado al usuario — separado de la clave interna de estado para no tocar la lógica de `getStatus`. */
const STATUS_LABELS: Record<VacationStatus, string> = {
  Activo:    'En curso',
  Próximo:   'Próximo',
  Finalizado:'Finalizado',
};

/** Texto explicativo del tooltip de cada chip de estado. */
const STATUS_TOOLTIPS: Record<VacationStatus, string> = {
  Próximo:   'Este período todavía no empezó.',
  Activo:    'Estás transitando este período en este momento.',
  Finalizado:'Este período ya terminó.',
};

/** Cómo mostrar quién hizo una edición — ver `VacationEditHistoryEntry.editedBy`. */
function editedByLabel(entry: VacationEditHistoryEntry): string {
  if (entry.editedBy === 'psychologist') return 'Vos';
  return entry.editedByName ? `El equipo de Psi Mammoliti (${entry.editedByName})` : 'El equipo de Psi Mammoliti';
}

/**
 * Arma la cadena de ediciones que llevaron al período `v` tal como está hoy —
 * más reciente primero. Correlaciona por fecha: busca la entrada cuyo
 * `newFrom/newTo` coincide con las fechas actuales de `v`, y desde ahí sigue
 * hacia atrás matcheando `previousFrom/previousTo` de una entrada contra
 * `newFrom/newTo` de la siguiente (soporta N ediciones sucesivas del mismo
 * período). Ver el trade-off de este matching en VacationEditHistory
 * (schema.prisma) — riesgo de borde aceptado con el usuario 2026-09-16.
 */
function findEditChain(v: VacationPeriod, history: VacationEditHistoryEntry[]): VacationEditHistoryEntry[] {
  const vFrom = v.from.substring(0, 10);
  const end = computeEnd(v);
  const vTo = end ? end.toISOString().substring(0, 10) : vFrom;

  const chain: VacationEditHistoryEntry[] = [];
  const used = new Set<string>();
  let cursorFrom = vFrom;
  let cursorTo = vTo;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const next = history.find(
      (h) => !used.has(h.id) && h.newFrom === cursorFrom && h.newTo === cursorTo,
    );
    if (!next) break;
    chain.push(next);
    used.add(next.id);
    cursorFrom = next.previousFrom;
    cursorTo = next.previousTo;
  }
  return chain;
}

/**
 * Bloque colapsable de auditoría para un grupo de `CancelledVacationSnapshot` (secundario
 * a propósito, no es el historial normal de la psicóloga). Se reusa para "antes de
 * gozarse" (`wasFuture: true`) y "estando en curso" (`wasFuture: false`) — mismo diseño,
 * distinto título — ver `cancelledFutureHistory`/`cancelledInProgressHistory` más abajo.
 */
interface CancelledHistorySectionProps {
  title: string;
  items: CancelledVacationSnapshot[];
  open: boolean;
  onToggle: () => void;
}

const CancelledHistorySection: React.FC<CancelledHistorySectionProps> = ({ title, items, open, onToggle }) => {
  if (items.length === 0) return null;
  return (
    <div className="mt-6 pt-4 border-t border-gray-100">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors uppercase tracking-wide"
      >
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        {title} ({items.length})
      </button>

      {open && (
        <div className="flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden mt-3">
          {items.map((c) => (
            <div key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 px-4 py-3 bg-gray-50">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                  <CalendarDays size={16} className="text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500 break-words line-through decoration-gray-300">
                    {formatShort(c.originalFrom)} → {formatShort(c.originalTo)}
                  </p>
                  <p className="text-xs text-gray-400">
                    Cancelado el {formatShort(c.cancelledAt)}{c.cancelledByName ? ` por el equipo de Psi Mammoliti (${c.cancelledByName})` : ''}
                  </p>
                </div>
              </div>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-200 text-gray-500 pl-12 sm:pl-0 flex-shrink-0">
                Cancelado
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProfileVacationsTab: React.FC<ProfileVacationsTabProps> = ({ userEmail, psychologistId, onVacationSaved, onGuideReplay, tourShowDemoModal }) => {
  // App mobile: sin onboarding guiado (solo web).
  const isPwaApp = useIsPwaApp();
  const [vacations, setVacations] = useState<VacationPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelledHistory, setCancelledHistory] = useState<CancelledVacationSnapshot[]>([]);
  const [cancelledHistoryOpen, setCancelledHistoryOpen] = useState(false);
  const [cancelledInProgressOpen, setCancelledInProgressOpen] = useState(false);
  const [editHistory, setEditHistory] = useState<VacationEditHistoryEntry[]>([]);
  const [openEditHistoryIds, setOpenEditHistoryIds] = useState<Set<string>>(new Set());
  const [hiddenLegacyIds, setHiddenLegacyIds] = useState<string[]>([]);
  // NZ, u O que viene de NZ: único canal es administración/NZ (WhatsApp de NZ, no el de CX de F/N/resto de O).
  const { email: contactEmail } = useContactChannel();

  // Modal de alta/edición — comparten el mismo componente (VacationPeriodFormModal).
  // `editVacation` no-null = editando un período existente; `createModalOpen` = alta nueva.
  // Solo uno de los dos puede estar activo a la vez.
  const [editVacation, setEditVacation] = useState<VacationPeriod | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const modalMode: VacationPeriodModalMode = editVacation ? 'edit' : 'create';
  const modalOpen = !!editVacation || createModalOpen;

  const fetchVacations = async () => {
    try {
      const res = await apiCoreAdapter.psychologistVacations({ psychologist: psychologistId, skip: 0, take: 100 }) as { results?: VacationPeriod[] } | VacationPeriod[];
      const raw: VacationPeriod[] = Array.isArray(res) ? res : (res as { results?: VacationPeriod[] }).results ?? [];
      const list = raw.filter(v => !v.cancelled);
      list.sort((a, b) => new Date(b.from).getTime() - new Date(a.from).getTime());
      setVacations(list);
    } catch {
      setVacations([]);
    }
  };

  /** Best-effort: si falla, las secciones secundarias simplemente no aparecen (no rompe
   *  el tab). Trae TODAS las cancelaciones (futuras y en curso, ver `wasFuture`) — el
   *  render las separa en dos grupos, ver `cancelledFutureHistory`/`cancelledInProgressHistory`. */
  const fetchCancelledHistory = async () => {
    try {
      const res = await apiCoreAdapter.psychologistVacationsCancelledHistory();
      setCancelledHistory(res?.results ?? []);
    } catch {
      setCancelledHistory([]);
    }
  };

  /** Best-effort: si falla, ningún período muestra el toggle de "Modificado" (no rompe el tab). */
  const fetchEditHistory = async () => {
    try {
      const res = await apiCoreAdapter.psychologistVacationsEditHistory();
      setEditHistory(res?.results ?? []);
    } catch {
      setEditHistory([]);
    }
  };

  const toggleEditHistory = (id: string) => {
    setOpenEditHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /** Best-effort: si falla, los fantasmas de cancelaciones ya conocidas simplemente no se ocultan (no rompe el tab). */
  const fetchHiddenLegacyIds = async () => {
    try {
      const res = await apiCoreAdapter.psychologistVacationsHiddenLegacyIds();
      setHiddenLegacyIds(res?.ids ?? []);
    } catch {
      setHiddenLegacyIds([]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        fetchVacations(),
        fetchCancelledHistory(),
        fetchEditHistory(),
        fetchHiddenLegacyIds(),
      ]);
      setLoading(false);
    })();
  }, []);

  /**
   * Excluye con CERTEZA (por id, no por fecha) los períodos que ya sabemos que
   * cancelamos nosotros mismos o que son fantasmas conocidos de antes de este
   * workaround — Make los muta IN PLACE (mismo id, from/to sobreescritos a la
   * fecha de cancelación) en vez de marcarlos de forma consistente
   * (`cancelled`/`deletedAt` no son confiables, ver memoria del proyecto), así
   * que sin esto el fantasma "hoy, 0 días" queda visible acá arriba. Usa
   * `hiddenLegacyIds` (TODOS los ids a ocultar) y no `cancelledHistory` (que
   * solo trae cancelaciones futuras con fecha, para la sección de auditoría) —
   * así también se ocultan cancelaciones de períodos ya en curso/pasados.
   */
  const hiddenLegacyIdsSet = new Set(hiddenLegacyIds);
  const visibleVacations = vacations.filter((v) => !hiddenLegacyIdsSet.has(v.id));

  /**
   * Hasta 2026-09-21 `cancelled-history` solo traía `wasFuture: true` — cancelar un
   * período YA EN CURSO generaba un snapshot que no aparecía en ningún lado visible
   * para la psicóloga (bug real, ver VacationsService.getCancelledHistoryForPsychologist).
   * Ahora el backend trae todo y acá se separa en dos secciones con copy distinto,
   * para no perder de vista ninguna cancelación.
   */
  const cancelledFutureHistory = cancelledHistory.filter((c) => c.wasFuture);
  const cancelledInProgressHistory = cancelledHistory.filter((c) => !c.wasFuture);

  const hasActive = visibleVacations.some(v => {
    const s = getStatus(v);
    return s === 'Activo' || s === 'Próximo';
  });

  /** Fechas iniciales del modal de edición — clampea `from` a hoy si el período
   *  ya arrancó, y descarta un `to` que ya pasó (mismo criterio que antes). */
  const editInitialDates = (v: VacationPeriod): { from: string; to: string } => {
    const todayStr = localToday();
    const fromStr = v.from.substring(0, 10);
    const clampedFrom = fromStr < todayStr ? todayStr : fromStr;
    const end = computeEnd(v);
    const toStr = end ? end.toISOString().substring(0, 10) : '';
    const clampedTo = toStr && toStr < todayStr ? '' : toStr;
    return { from: clampedFrom, to: clampedTo };
  };

  const [modalInitialFrom, setModalInitialFrom] = useState('');
  const [modalInitialTo, setModalInitialTo] = useState('');

  const openEdit = (v: VacationPeriod) => {
    const { from, to } = editInitialDates(v);
    setModalInitialFrom(from);
    setModalInitialTo(to);
    setEditVacation(v);
  };

  const openCreate = () => {
    setModalInitialFrom('');
    setModalInitialTo('');
    setCreateModalOpen(true);
  };

  // Tour: abre el modal "Informar vacaciones" con un período ficticio de 20 días —
  // mismo ejemplo ilustrativo que ya usa el resto de la app (ver
  // VACATION_BLOCK_ILLUSTRATIVE_EXAMPLES.from16to30) — para que el cuadro de
  // "Consecuencias de tu solicitud" se vea poblado y resaltando la fila correcta,
  // en vez de mostrar el formulario vacío. Se cierra solo al salir de ese paso.
  useEffect(() => {
    if (!tourShowDemoModal) {
      setCreateModalOpen(false);
      return;
    }
    const from = new Date();
    from.setDate(from.getDate() + 30);
    const to = new Date();
    to.setDate(to.getDate() + 50); // +20 días respecto de `from`
    setModalInitialFrom(dateToLocalStr(from));
    setModalInitialTo(dateToLocalStr(to));
    setCreateModalOpen(true);
  }, [tourShowDemoModal]);

  const closeModal = () => {
    if (saving) return;
    setEditVacation(null);
    setCreateModalOpen(false);
  };

  /** Arma el mismo mail HTML de consentimiento que ya se manda al editar —
   *  reusado tal cual para el alta (mismo criterio: registro + tabla de
   *  consecuencias + rango elegido). Solo cambia el asunto/encabezado.
   *  `previous` es opcional y SOLO se pasa desde `handleEditConfirm` — agrega
   *  una sección "Antes → Ahora" al mail; el mail de alta (`handleCreateConfirm`)
   *  no lo pasa nunca, así que su HTML queda idéntico a como estaba. */
  const sendConsentMail = async (
    from: string,
    to: string,
    subject: string,
    headerText: string,
    previous?: { from: string; to: string },
  ) => {
    const consentNow = new Date();
    const fechaConsentimiento =
      consentNow.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
      ' a las ' +
      consentNow.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const fromDateObj = new Date(from + 'T12:00:00');
    const toDateObj = to ? new Date(to + 'T12:00:00') : null;
    const blockResult = toDateObj ? computeVacationBlockDates(fromDateObj, toDateObj) : null;
    const emailDays = blockResult ? blockResult.days : null;
    const fromDisplay = formatShort(from);
    const toDisplay = to ? formatShort(to) : 'sin fecha de fin';
    const emailActiveRow = blockResult ? VACATION_BLOCK_BRACKETS.indexOf(blockResult.bracket) : null;
    const detailSentence = toDateObj ? formatVacationBlockSentence(fromDateObj, toDateObj, formatVacationDate) : null;
    const previousSectionHtml = previous
      ? `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f9fafb;border-radius:8px;margin-bottom:16px;">
              <tr><td style="padding:14px 20px;">
                <p style="margin:0 0 6px;font-size:12px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Período anterior a esta modificación</p>
                <p style="margin:0;font-size:14px;color:#6b7280;text-decoration:line-through;font-family:Arial,Helvetica,sans-serif;">
                  ${formatShort(previous.from)} → ${formatShort(previous.to)}
                </p>
              </td></tr>
            </table>`
      : '';
    const vacTableRows = VACATION_BLOCK_BRACKETS.map((bracket, i) => {
      const isActive = emailActiveRow === i;
      const bg = isActive ? 'background-color:#F0EEFB;' : '';
      const color = isActive ? 'color:#5a47a8;font-weight:600;' : 'color:#374151;';
      return `<tr style="${bg}">
        <td style="padding:10px 14px;font-size:13px;${color}border-bottom:1px solid #e5e7eb;">${bracket.label}${isActive ? ' ◀' : ''}</td>
        <td style="padding:10px 14px;font-size:13px;${color}border-bottom:1px solid #e5e7eb;">${bracket.blockBeforeLabel}</td>
        <td style="padding:10px 14px;font-size:13px;${color}border-bottom:1px solid #e5e7eb;">${bracket.blockAfterLabel}</td>
      </tr>`;
    }).join('');
    const vacConsentHtml = `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
        <tr>
          <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
            <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
              ${headerText}
            </p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 36px 8px;">
            <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">Hola,</p>
            <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
              Te enviamos este mail para dejar registro de que el <strong>${fechaConsentimiento}</strong>
              confirmaste tu período de vacaciones en Psi Platform.
            </p>
            ${previousSectionHtml}
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
              <tr><td style="padding:16px 20px;">
                <p style="margin:0 0 8px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">${previous ? 'Período nuevo' : 'Período solicitado'}</p>
                <p style="margin:0;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                  <strong>${fromDisplay}</strong> → <strong>${toDisplay}</strong>${emailDays !== null ? ` (${emailDays} ${emailDays === 1 ? 'día' : 'días'})` : ''}
                </p>
              </td></tr>
            </table>
            <p style="margin:0 0 12px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Consecuencias según duración</p>
            <div style="overflow-x:auto;border-radius:10px;border:1px solid #e5e7eb;margin-bottom:24px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr style="background-color:#f9fafb;">
                  <th style="padding:10px 14px;font-size:12px;font-weight:700;color:#374151;text-align:left;border-bottom:1px solid #e5e7eb;">${VACATION_BLOCK_TABLE_HEADERS.days}</th>
                  <th style="padding:10px 14px;font-size:12px;font-weight:700;color:#374151;text-align:left;border-bottom:1px solid #e5e7eb;">${VACATION_BLOCK_TABLE_HEADERS.before}</th>
                  <th style="padding:10px 14px;font-size:12px;font-weight:700;color:#374151;text-align:left;border-bottom:1px solid #e5e7eb;">${VACATION_BLOCK_TABLE_HEADERS.after}</th>
                </tr>
                ${vacTableRows}
              </table>
            </div>
            ${detailSentence ? `<p style="margin:0 0 24px;font-size:13px;color:#7C3AED;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">▶ ${detailSentence}</p>` : ''}
            <p style="margin:0 0 24px;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
              Guardá este mail como constancia. Si tenés alguna duda escribinos a
              <a href="mailto:${contactEmail}" style="color:#7C3AED;font-weight:700;text-decoration:none;">${contactEmail}</a>.
            </p>
          </td>
        </tr>
        <tr><td style="padding:8px 36px 28px;"><p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">Equipo Psi Mammoliti</p></td></tr>
        <tr><td style="padding:0 36px 16px;"><p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">Por favor no respondas este mensaje, ya que es automático. Cualquier inquietud podés contactarte a <a href="mailto:${contactEmail}" style="color:#888888;text-decoration:underline;">${contactEmail}</a>.</p></td></tr>
        <tr><td style="padding:0 36px 24px;"><hr style="border:none;border-top:1px solid #e8e8e8;margin:0 0 16px;" /><p style="margin:0;font-size:11px;color:#aaaaaa;text-align:center;font-family:Arial,Helvetica,sans-serif;"><a href="#" style="color:#aaaaaa;text-decoration:underline;">Click here to unsubscribe</a></p></td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
    void apiCoreAdapter.psychologistNotifyProfileChanges({
      html: vacConsentHtml,
      subject,
      from: 'no-responder@psimammoliti.com',
    });
  };

  /** Editar un período existente — sin cambios de comportamiento respecto a
   *  antes, solo recibe (from, to) del modal en vez de leerlos de estado local. */
  const handleEditConfirm = async (from: string, to: string) => {
    if (!editVacation || !from) return;
    setSaving(true);
    try {
      // Capturadas ANTES de que el webhook las reemplace — es el único momento
      // en que conocemos con certeza el período real previo (ver VacationEditHistory).
      const previousFrom = editVacation.from.substring(0, 10);
      const previousEnd = computeEnd(editVacation);
      const previousTo = previousEnd ? previousEnd.toISOString().substring(0, 10) : previousFrom;

      const body = {
        from: from + 'T00:00:00.000Z',
        to: to ? to + 'T00:00:00.000Z' : undefined,
        psychologist_id: psychologistId,
        vacations: editVacation.id,
      };
      const res = await fetch(PROFILE_EDIT_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error al guardar');
      toastUtils.success('Vacaciones actualizadas correctamente');

      await sendConsentMail(
        from,
        to,
        'Confirmación de modificación de vacaciones',
        'Modificación de vacaciones registrada ✅',
        { from: previousFrom, to: previousTo },
      );

      // Rastro de auditoría — fire-and-forget, no debe frenar el flujo real si falla.
      void apiCoreAdapter.psychologistVacationsLogEdit({
        previousVacationId: editVacation.id,
        previousFrom,
        previousTo,
        newFrom: from,
        newTo: to || from,
      });

      closeModal();
      await fetchVacations();
      await fetchEditHistory();
      onVacationSaved?.();
    } catch {
      toastUtils.error('No se pudo guardar. Intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Alta nativa de un período — pega al webhook clon (mismo escenario de
   * negocio que Tally, trigger reemplazado por un webhook directo, ver
   * memoria del proyecto). A diferencia de `handleEditConfirm`, el escenario
   * SÍ puede rechazar la carga con status 200 y `{error: "..."}` en el body
   * (from > to, fecha pasada) — un 200 "ok" HTTP no implica éxito real acá,
   * así que hay que parsear el body antes de festejar. El éxito real no
   * devuelve JSON (Make responde su "Accepted" default de texto plano), por
   * eso el parseo de JSON está en un try/catch: si falla, es éxito.
   */
  const handleCreateConfirm = async (from: string, to: string) => {
    if (!from) return;
    // Métricas de Actividad (funnel vacaciones): envía el formulario.
    activityTracker.trackAction('vacation_requested');
    setSaving(true);
    try {
      const body = {
        email: userEmail,
        from: from + 'T00:00:00.000Z',
        to: to ? to + 'T00:00:00.000Z' : undefined,
      };
      const res = await fetch(VACATION_CREATE_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error al guardar');

      const text = await res.text();
      let scenarioError: string | null = null;
      try {
        const parsed = JSON.parse(text) as { error?: string };
        if (parsed?.error?.trim()) scenarioError = parsed.error;
      } catch {
        // No es JSON (ej. "Accepted") — respuesta normal de éxito, sin error que leer.
      }

      if (scenarioError) {
        toastUtils.error(scenarioError);
        return; // Dejamos el modal abierto para que corrija las fechas — no se guardó nada.
      }

      toastUtils.success('Vacaciones informadas correctamente');
      activityTracker.trackAction('vacation_confirmed'); // el escenario respondió sin error: quedó registrado
      await sendConsentMail(from, to, 'Confirmación de alta de vacaciones', 'Alta de vacaciones registrada ✅');

      closeModal();
      await fetchVacations();
      onVacationSaved?.();
    } catch {
      toastUtils.error('No se pudo guardar. Intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  // Períodos ya cargados (sin cancelar) para el chequeo de solapamiento del modal
  // — en modo edición se excluye el propio período que se está editando.
  const existingPeriodsForModal: ExistingVacationPeriod[] = visibleVacations
    .filter(v => v.id !== editVacation?.id)
    .map(v => {
      const end = computeEnd(v);
      return {
        id: v.id,
        from: v.from.substring(0, 10),
        to: end ? end.toISOString().substring(0, 10) : v.from.substring(0, 10),
      };
    });

  return (
    <div id="profile-vacations-section">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="section-title">Vacaciones y Días off</h3>
        {onGuideReplay && !isPwaApp && (
          <button
            type="button"
            onClick={onGuideReplay}
            title="Repasar guía de Vacaciones y Días off"
            aria-label="replay-vacations-onboarding"
            className="flex-shrink-0 inline-flex items-center gap-1 text-[0.75rem] font-normal text-[#8475b3] hover:text-[#6D57BC] bg-transparent border-0 cursor-pointer transition-colors"
          >
            <Info size={14} />
            Repasar guía de Vacaciones y Días off
          </button>
        )}
      </div>

      {/* Botón principal */}
      <button
        type="button"
        id="vacations-tour-inform-btn"
        onClick={openCreate}
        className="inline-flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors mb-5"
      >
        <CalendarDays size={15} className="text-gray-500" />
        Informar vacaciones &amp; días off
        <ArrowUpRight size={14} className="text-gray-400" />
      </button>

      {/* Banner informativo si hay período activo/próximo */}
      {hasActive && (
        <div className="flex items-start gap-3 bg-[#F0EEFB] border border-[#D6CFF5] rounded-xl px-4 py-3 mb-5 text-sm text-[#5a47a8]">
          <Info size={16} className="mt-0.5 flex-shrink-0 text-[#6D57BC]" />
          <p className="m-0 leading-snug">
            Estás transitando un período de días off o entrarás en uno en el corto plazo. Esto limita la asignación de nuevos pacientes.
          </p>
        </div>
      )}

      {/* Lista */}
      <div id="vacations-tour-list">
      <p className="text-sm font-semibold text-gray-700 pb-5">Detalle vacaciones &amp; días off</p>

      {loading ? (
        <div className="flex justify-center py-8">
          <CircularProgress sizeRem={1.75} color="#6D57BC" label="Cargando..." />
        </div>
      ) : visibleVacations.length === 0 ? (
        <p className="text-sm text-gray-400 italic py-4">No hay períodos registrados.</p>
      ) : (
        <>
          <div className="flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
            {visibleVacations.map((v) => {
              const status = getStatus(v);
              const days = diffDays(v);
              const endDate = computeEnd(v);
              const editChain = findEditChain(v, editHistory);
              const historyOpen = openEditHistoryIds.has(v.id);
              return (
                <div key={v.id} className="bg-white hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 px-4 py-3">
                    {/* Icono + Fechas */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#EDE9FA] flex items-center justify-center">
                        <CalendarDays size={16} className="text-[#6D57BC]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 break-words">
                          {formatShort(v.from)}{endDate ? ` → ${formatShort(endDate.toISOString())}` : ''}
                        </p>
                        <p className="text-xs text-gray-400">{days} {days === 1 ? 'día' : 'días'}</p>
                      </div>
                    </div>

                    {/* Badge + acción — indented on mobile to align under text */}
                    <div className="flex items-center gap-2 pl-12 sm:pl-0 sm:flex-shrink-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLES[status]}`}>
                            {STATUS_LABELS[status]}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{STATUS_TOOLTIPS[status]}</TooltipContent>
                      </Tooltip>
                      {(() => {
                        const isFinalized = status === 'Finalizado';
                        const modificarButton = (
                          <button
                            type="button"
                            onClick={() => { if (!isFinalized) openEdit(v); }}
                            aria-disabled={isFinalized}
                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                              isFinalized ? 'text-gray-400 cursor-not-allowed' : 'text-[#6D57BC] hover:underline cursor-pointer'
                            }`}
                          >
                            <Pencil size={12} />
                            Modificar
                          </button>
                        );
                        if (!isFinalized) return modificarButton;
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>{modificarButton}</TooltipTrigger>
                            <TooltipContent>No se pueden modificar períodos finalizados.</TooltipContent>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Toggle "Modificado" — solo aparece si este período tiene historial de
                      ediciones (ver findEditChain). Colapsado por default, a propósito, para
                      no ensuciar la vista principal de un período que nunca se tocó. */}
                  {editChain.length > 0 && (
                    <div className="pl-16 pr-4 pb-3 -mt-1">
                      <button
                        type="button"
                        onClick={() => toggleEditHistory(v.id)}
                        className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <ChevronDown size={12} className={`transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
                        Modificado{editChain.length > 1 ? ` (${editChain.length}x)` : ''} — ver versión anterior
                      </button>
                      {historyOpen && (
                        <div className="mt-2 flex flex-col gap-2 border-l-2 border-gray-200 pl-3">
                          {editChain.map((entry) => (
                            <div key={entry.id} className="text-xs text-gray-500">
                              <p className="m-0 line-through decoration-gray-300">
                                {formatShort(entry.previousFrom)} → {formatShort(entry.previousTo)}
                              </p>
                              <p className="m-0 text-gray-400">
                                Modificado por {editedByLabel(entry)} el {formatShort(entry.editedAt)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 text-right pt-4">
            {visibleVacations.length} {visibleVacations.length === 1 ? 'registro' : 'registros'}
          </p>
        </>
      )}
      </div>

      {/* Secundarias y colapsadas a propósito: son períodos que la psicóloga había
          cargado y se cancelaron — no son su historial normal, solo un rescate de
          auditoría (ver CancelledVacationSnapshot / memoria del proyecto sobre el bug
          de Make que corrompe from/to al cancelar). Separadas en dos grupos porque
          cancelar algo YA EN CURSO es un caso distinto de cancelar algo futuro (bug
          real hasta 2026-09-21: el segundo grupo no aparecía en ningún lado). Cada
          una solo se renderiza si tiene algo que mostrar. */}
      <CancelledHistorySection
        title="Cancelados antes de gozarse"
        items={cancelledFutureHistory}
        open={cancelledHistoryOpen}
        onToggle={() => setCancelledHistoryOpen((o) => !o)}
      />
      <CancelledHistorySection
        title="Cancelados estando en curso"
        items={cancelledInProgressHistory}
        open={cancelledInProgressOpen}
        onToggle={() => setCancelledInProgressOpen((o) => !o)}
      />

      <VacationPeriodFormModal
        open={modalOpen}
        mode={modalMode}
        initialFrom={modalInitialFrom}
        initialTo={modalInitialTo}
        saving={saving}
        onClose={closeModal}
        onConfirm={modalMode === 'edit' ? handleEditConfirm : handleCreateConfirm}
        existingPeriods={existingPeriodsForModal}
        tourDemo={!!tourShowDemoModal}
      />
    </div>
  );
};

export default ProfileVacationsTab;
